/**
 * Generate the claim QR for a print's card back.
 *
 * Renders the branded styled QR (rounded dots + custom finder eyes) in
 * the configured foreground color at high error-correction so it stays
 * scannable, then punches a white rounded "well" in the center for the
 * chest logo overlay (layered separately in the DOM so it stays crisp).
 * Returns a PNG data URL sized for crisp ~3" print output.
 */

import { drawStyledQR, roundRect } from "@/lib/qr-renderer";

export async function generateClaimQR(
  claimUrl: string,
  pixelSize: number,
  foregroundColor: string,
): Promise<string> {
  const canvas = document.createElement("canvas");
  canvas.width = pixelSize;
  canvas.height = pixelSize;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Could not get canvas context");

  drawStyledQR(ctx, 0, 0, {
    text: claimUrl,
    size: pixelSize,
    margin: 1,
    errorCorrectionLevel: "H",
    foregroundColor,
  });

  // White rounded-square logo well — sized to fit the 32% logo overlay
  // with ~12% padding on every side, with a subtle 18% corner radius so
  // it visually pairs with the QR's own rounded dots.
  const logoSize = pixelSize * 0.32;
  const wellPadding = logoSize * 0.12;
  const wellSize = logoSize + wellPadding * 2;
  const wellX = (pixelSize - wellSize) / 2;
  const wellY = (pixelSize - wellSize) / 2;
  const wellRadius = wellSize * 0.18;
  ctx.fillStyle = "#FFFFFF";
  roundRect(ctx, wellX, wellY, wellSize, wellSize, wellRadius);
  ctx.fill();

  return canvas.toDataURL("image/png", 1.0);
}
